/**
 * NOTE: This code belongs to Kangaroo Rewards. Unauthorized use is prohibited
 */

@file:Suppress("unused", "MemberVisibilityCanBePrivate", "EXPERIMENTAL_API_USAGE")
@file:OptIn(ExperimentalJsExport::class)
package features.userBusinessNotifications

import kangaroorewards.appsdk.core.api.Api
import kangaroorewards.appsdk.core.domain.Result
import kangaroorewards.appsdk.core.domain.Result.Idle
import kangaroorewards.appsdk.core.domain.Result.Loading
import kangaroorewards.appsdk.core.domain.SerializedResult
import kangaroorewards.appsdk.core.domain.toJsonResult
import kangaroorewards.appsdk.core.utils.CFlow
import kangaroorewards.appsdk.core.utils.asCFlow
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.mapLatest
import kotlinx.serialization.InternalSerializationApi
import kotlin.js.ExperimentalJsExport
import features.userBusinessNotifications.internal.UserBusinessNotificationsUseCase
import features.userBusinessNotifications.internal.UserBusinessNotificationsUseCase.Params
import features.userBusinessNotifications.models.UserBusinessNotificationsModel


typealias UserBusinessNotificationsState = CFlow<UserBusinessNotificationsUseCaseResult?>

typealias UserBusinessNotificationsUseCaseResult = Result<UserBusinessNotificationsModel>

fun UserBusinessNotificationsUseCaseResult.serializeUserBusinessNotificationsApiResult(): SerializedResult<String> {
    return this.toJsonResult<UserBusinessNotificationsModel>()
}

/**
 * Serializes [UserBusinessNotificationsState]'s data and returns a new
 * [CFlow] containing data serialized as a JSON [String].
 */
@OptIn(ExperimentalCoroutinesApi::class)
fun UserBusinessNotificationsState.serializeUserBusinessNotificationsState(): CFlow<SerializedResult<String>?> {
    return this.mapLatest {
        it?.toJsonResult<UserBusinessNotificationsModel>()
    }.asCFlow()
}

/**
 * Get a list of notifications available to this user by specific business. [Api] class
 */
class UserBusinessNotificationsApi : Api() {

    val userBusinessNotificationsState: UserBusinessNotificationsState
        get() = _userBusinessNotificationsState.asCFlow()

    companion object {
        private val _userBusinessNotificationsState: MutableStateFlow<UserBusinessNotificationsUseCaseResult> = MutableStateFlow(Idle())
    }

    private val userBusinessNotificationsUseCase = UserBusinessNotificationsUseCase()

    /**
     * Get a list of notifications available to this user by specific business.
     */
    suspend fun getUserBusinessNotifications(
        overrideHeaders: Map<String, String>? = null,
        businessId: String
    ): Result<UserBusinessNotificationsModel> {
        /* set state to Loading as soon as function is called */
        _userBusinessNotificationsState.value = Loading()
        val result = this.userBusinessNotificationsUseCase(
            Params(
                overrideHeaders = overrideHeaders,
                businessId = businessId
            )
        ) { result ->
            _userBusinessNotificationsState.value = result
            return@userBusinessNotificationsUseCase result
        }
        return result
    }

}
